import { Alert, Box, Button, Stack } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RepeatOutlinedIcon from "@mui/icons-material/RepeatOutlined";
import AppLoadingOverlay from "../../components/loaders/AppLoadingOverlay";
import ExpenseFormModal from "../../components/modals/ExpenseFormModal";
import RecurringExpenseFormModal from "../../components/modals/RecurringExpenseFormModal";
import { PageHeader } from "../../components/cards/PageHeader";
import AnalyticsHeroCard from "../../components/statistics/AnalyticsHeroCard";
import AnalyticsMetricGrid from "../../components/statistics/AnalyticsMetricGrid";
import AnalyticsPieChart from "../../components/statistics/AnalyticsPieChart";
import AnalyticsCashflowChart from "../../components/statistics/AnalyticsCashflowChart";
import AnalyticsBreakdownCard from "../../components/statistics/AnalyticsBreakdownCard";
import StatisticsFiltersCard from "../../components/statistics/StatisticsFiltersCard";
import StatisticsFilterDialog from "../../components/statistics/StatisticsFilterDialog";
import StatisticsFloatingActions from "../../components/statistics/StatisticsFloatingActions";
import ExpenseActionDialog from "../../components/statistics/ExpenseActionDialog";
import StatisticsExpenseLists from "../../components/statistics/StatisticsExpenseLists";
import { useStatisticsScreen } from "./hooks/useStatisticsScreen";

const StatisticsScreen = () => {
  const vm = useStatisticsScreen();

  return (
    <Box sx={{ pb: { xs: 10, md: 0 } }}>
      <AppLoadingOverlay visible={vm.loading} />

      <PageHeader
        title={vm.t("statistics.title")}
        action={
          <Stack
            direction="row"
            spacing={1}
            sx={{ display: { xs: "none", md: "flex" } }}
          >
            <Button
              variant="outlined"
              startIcon={<RepeatOutlinedIcon />}
              onClick={() => vm.openRecurringExpenseModal()}
              disabled={vm.loading}
            >
              {vm.t("expenses.newRecurringExpense")}
            </Button>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => vm.openExpenseModal()}
              disabled={vm.loading}
            >
              {vm.t("expenses.newExpense")}
            </Button>
          </Stack>
        }
      />

      {vm.error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {vm.error}
        </Alert>
      ) : null}

      <StatisticsFiltersCard
        from={vm.from}
        until={vm.until}
        setFrom={vm.setFrom}
        setUntil={vm.setUntil}
        days={vm.days}
        setDateRangeFromToday={vm.setDateRangeFromToday}
        setThisMonth={vm.setThisMonth}
        selectedPropertyId={vm.selectedPropertyId}
        setSelectedPropertyId={vm.setSelectedPropertyId}
        selectedUnitId={vm.selectedUnitId}
        setSelectedUnitId={vm.setSelectedUnitId}
        groupBy={vm.groupBy}
        setGroupBy={vm.setGroupBy}
        paymentStatus={vm.paymentStatus}
        setPaymentStatus={vm.setPaymentStatus}
        properties={vm.properties}
        filteredUnits={vm.filteredUnits}
        t={vm.t}
      />

      <AnalyticsHeroCard overview={vm.overview} t={vm.t} />

      <AnalyticsMetricGrid overview={vm.overview} t={vm.t} />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
          gap: 2,
          mb: 2,
        }}
      >
        <AnalyticsPieChart
          title={vm.t("statistics.revenueByApartment")}
          items={vm.summary?.charts?.revenue_by_unit || []}
          emptyText={vm.t("statistics.noRevenueForPeriod")}
        />

        <AnalyticsPieChart
          title={vm.t("statistics.expensesByCategory")}
          items={vm.summary?.charts?.expenses_by_category || []}
          emptyText={vm.t("statistics.noExpensesForPeriod")}
        />

        <AnalyticsPieChart
          title={vm.t("statistics.manualVsRecurringExpenses")}
          items={vm.summary?.charts?.expenses_by_type || []}
          emptyText={vm.t("statistics.noExpensesForPeriod")}
        />

        <AnalyticsPieChart
          title={vm.t("expenses.expensesByUnit")}
          items={vm.summary?.charts?.expenses_by_unit || []}
          emptyText={vm.t("statistics.noExpensesForPeriod")}
        />
      </Box>

      <AnalyticsCashflowChart
        items={vm.summary?.charts?.monthly_cashflow || []}
        currency={vm.overview.currency}
        t={vm.t}
      />

      <AnalyticsBreakdownCard items={vm.summary?.breakdown || []} t={vm.t} />

      <StatisticsExpenseLists
        expenses={vm.expenses}
        recurringExpenses={vm.recurringExpenses}
        openExpenseModal={vm.openExpenseModal}
        openRecurringExpenseModal={vm.openRecurringExpenseModal}
        confirmDeleteExpense={vm.confirmDeleteExpense}
        confirmDeleteRecurringExpense={vm.confirmDeleteRecurringExpense}
        t={vm.t}
      />

      <StatisticsFloatingActions
        visible={!vm.loading}
        onFilterPress={() => vm.setFilterDialogOpen(true)}
        onAddPress={() => vm.setExpenseActionDialogOpen(true)}
      />

      <StatisticsFilterDialog
        open={vm.isFilterDialogOpen}
        onClose={() => vm.setFilterDialogOpen(false)}
        from={vm.from}
        until={vm.until}
        setFrom={vm.setFrom}
        setUntil={vm.setUntil}
        days={vm.days}
        setDateRangeFromToday={vm.setDateRangeFromToday}
        setThisMonth={vm.setThisMonth}
        selectedPropertyId={vm.selectedPropertyId}
        setSelectedPropertyId={vm.setSelectedPropertyId}
        selectedUnitId={vm.selectedUnitId}
        setSelectedUnitId={vm.setSelectedUnitId}
        groupBy={vm.groupBy}
        setGroupBy={vm.setGroupBy}
        paymentStatus={vm.paymentStatus}
        setPaymentStatus={vm.setPaymentStatus}
        properties={vm.properties}
        filteredUnits={vm.filteredUnits}
        onApply={vm.refreshAll}
        t={vm.t}
      />

      <ExpenseActionDialog
        open={vm.isExpenseActionDialogOpen}
        onClose={() => vm.setExpenseActionDialogOpen(false)}
        onOneTimePress={() => vm.openExpenseModal()}
        onRecurringPress={() => vm.openRecurringExpenseModal()}
        t={vm.t}
      />

      <ExpenseFormModal
        open={vm.expenseModalOpen}
        loading={vm.loading}
        properties={vm.properties}
        units={vm.units}
        expense={vm.selectedExpense}
        defaultCurrency={vm.defaultCurrency}
        onClose={vm.closeExpenseModal}
        onSubmit={vm.submitExpense}
      />

      <RecurringExpenseFormModal
        open={vm.recurringExpenseModalOpen}
        loading={vm.loading}
        properties={vm.properties}
        units={vm.units}
        recurringExpense={vm.selectedRecurringExpense}
        defaultCurrency={vm.defaultCurrency}
        onClose={vm.closeRecurringExpenseModal}
        onSubmit={vm.submitRecurringExpense}
      />
    </Box>
  );
};

export default StatisticsScreen;
